import React, { useEffect, useState } from "react";
import Head from "next/head";
import { Document, Page, pdfjs } from "react-pdf";
import PDFMerger from "pdf-merger-js/browser";
import { ReactSortable } from "react-sortablejs";
import { AiOutlineClose, AiOutlineMergeCells } from "react-icons/ai";
import { FaFileUpload } from "react-icons/fa";
import styles from "../../signature.module.css";
import SignaturePopup from "../../../../components/SignaturePopup";
import Blog from "../../../../components/CalculatorBlogs/Blog";
import PdfToolsLinks from "../../../../components/PdfToolsLinks";

import UploadFile from "@components/PdfTools/UploadFile";
import Steps from "@components/PdfTools/Steps";
import WhyChooseBolosign from "@components/PdfTools/WhyChooseBolosign";
import PricingCards from "@components/PdfTools/PricingCards";
import TryBolosign from "@components/PdfTools/TryBolosign";
import OtherPdfTools from "@components/PdfTools/OtherPdfTools";
import FaqCard from "@components/Cards/FaqCard";
import { LuUpload } from "react-icons/lu";
import { LuDownload } from "react-icons/lu";
import { GrSort } from "react-icons/gr";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const steps = [
  {
    icon: <LuUpload />,
    title: "Upload PDFs",
    description:
      "Select the PDF files you want to merge from your computer or drag and drop them into the upload area.",
  },
  {
    icon: <GrSort />,
    title: "Reorder PDFs",
    description:
      "Easily reorder the files by dragging them into the desired sequence.",
  },
  {
    icon: <LuDownload />,
    title: "Download PDF",
    description:
      "Click the 'Merge PDF' button, and your combined document will be ready for download in seconds.",
  },
];

const reasons = [
  {
    image: "combine-multiple.png",
    title: "Combine Multiple PDFs",
    description:
      "Merge multiple PDF documents into one cohesive file effortlessly.",
  },
  {
    image: "three-quick-steps.png",
    title: "Three Quick Steps",
    description:
      "Just upload your files, reorder your files and download your files altogether.",
  },
  {
    image: "cross-platform.png",
    title: "Cross-Platform Compatibility",
    description:
      "Access our tool from any device, whether you're using a desktop, tablet, or smartphone.",
  },
  {
    image: "high-quality-output.png",
    title: "High-Quality Output",
    description:
      "Ensure that your merged PDF retains the original quality of each individual file.",
  },
];

const faqData = [
  {
    faqQuestion: "Can I merge PDF documents for free with BoloSign?",
    faqAnswer:
      "Yes, BoloSign offers a free tool to merge PDF documents without any cost.",
  },
  {
    faqQuestion: "Is it safe to merge PDFs using BoloSign?",
    faqAnswer:
      "Absolutely. We prioritize your privacy and ensure that your documents are processed securely.",
  },
  {
    faqQuestion: "Do I need to create an account to merge PDFs with BoloSign?",
    faqAnswer:
      "No, you can merge your PDF documents without creating an account. However, signing up allows you to access additional features.",
  },
  {
    faqQuestion: "Can I merge PDFs on my mobile device?",
    faqAnswer:
      "Yes, BoloSign’s PDF merge tool is compatible with all devices, including smartphones and tablets.",
  },
];

const MergePdf = () => {
  const [pdfFiles, setPdfFiles] = useState([]);
  const [mergeLoader, setMergeLoader] = useState(false);
  const [signaturePopup, setSignaturePopup] = useState(false);

  const scrollToPreview = () => {
    const element = document.getElementById("preview-pdfs");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const mergePdfs = async () => {
    setMergeLoader(true);
    const merger = new PDFMerger();
    for (const file of pdfFiles) {
      await merger.add(file);
    }
    const mergedPdf = await merger.saveAsBlob();
    const url = URL.createObjectURL(mergedPdf);
    await downloadMergedPdf(url);
    setMergeLoader(false);
  };

  // download merged pdf
  const downloadMergedPdf = async (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "merged.pdf");
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => {
      setSignaturePopup(true);
    }, 2000);
  };

  const handleFileUpload = (files) => {
    setPdfFiles([...pdfFiles, ...files]);
    if (files.length > 0) {
      scrollToPreview();
    }
  };

  return (
    <div className="w-full mt-20 sm:mt-24">
      <Head>
        <meta charSet="utf-8" />
        <title>Online PDF merge tool - BoloForms</title>
        <meta
          name="description"
          content="The fastest and easiest PDF merger lets you combine PDFs in the order you want."
        />
        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="Boloforms" />
        <meta name="publisher" content="BoloForms" />
        <meta
          name="keywords"
          content="Online PDF merge tool, Online PDF merge tool 2022, Online PDF merge tool 2023, Online PDF merge tool BoloForms, Online PDF merge tool BoloForms.com, Merge Pdf online, Merge Pdf, Merge pdf online boloforms"
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/pdf-tools/merge-pdf/"
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Online PDF merge tool - BoloForms" />
        <meta name="twitter:site" content="@boloforms" />
        <meta
          property="twitter:url"
          content="https://www.bolosign.com/signature/pdf-tools/merge-pdf/"
        />
        <meta
          property="twitter:title"
          content="Online PDF merge tool - BoloForms"
        />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/pdf-tools/merge-pdf/"
        />
      </Head>

      <div className="w-full">
        <UploadFile
          heading={`Effortlessly Merge PDF Documents with BoloSign`}
          description={`Combining multiple PDF files into a single document has never been easier with BoloSign. Whether you need to merge PDF documents for business reports, academic papers, or personal projects, our tool provides a seamless and efficient solution.`}
          bgColor={"bg-[#fee5de]"}
          btnColor={"bg-[#ffb19a]"}
          image={"merge-pdf.png"}
          handleFileUpload={handleFileUpload}
        />
        <div
          id="preview-pdfs"
          className="max-w-[1280px] mx-auto flex flex-col gap-14 justify-center items-center mt-12 sm:mt-20 px-4 sm:px-8"
        >
          {pdfFiles.length > 0 && (
            <>
              <h1 className="text-2xl sm:text-3xl font-light text-center text-gray-900">
                Drag and Sort to change the order
              </h1>

              <ReactSortable
                list={pdfFiles}
                setList={setPdfFiles}
                className="w-full flex flex-wrap justify-center gap-10 md:border-2 md:px-10 md:py-14 md:rounded-xl cursor-grabbing"
              >
                {pdfFiles.map((file, index) => (
                  <div key={index} className="border w-fit relative">
                    <Document file={file}>
                      <Page
                        pageNumber={1}
                        width={260}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                    <div className="absolute top-2 left-2 w-8 h-8 bg-black rounded-full text-xl text-white flex justify-center items-center cursor-pointer">
                      {index + 1}
                    </div>
                    <div
                      onClick={() =>
                        setPdfFiles(pdfFiles.filter((_, i) => i !== index))
                      }
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex justify-center items-center cursor-pointer"
                    >
                      <AiOutlineClose className="text-white text-xl" />
                    </div>
                    <div className="absolute bottom-0 left-0 w-full h-12 bg-black overflow-hidden p-2 flex items-center">
                      <p className="text-white text-sm">
                        {file.name || "Untitled.pdf"}
                      </p>
                    </div>
                  </div>
                ))}
              </ReactSortable>
            </>
          )}
          {pdfFiles.length > 1 && (
            <div id="merge-pdfs" className="w-full">
              <button
                onClick={mergePdfs}
                className="bg-[#ffb19a] mx-auto w-full sm:w-[350px] py-4 text-xl rounded-lg font-semibold text-gray-900 flex items-center justify-center gap-4 hover:opacity-90 border-2 border-gray-900"
              >
                {mergeLoader ? (
                  <div
                    className={`flex items-center text-white space-x-2 ${styles.loader}`}
                  >
                    <div className="">Merging</div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                  </div>
                ) : (
                  <>
                    Merge Files <AiOutlineMergeCells className="text-3xl" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div className="w-full px-4 sm:px-8">
          <Steps
            heading={"How to Merge PDF Documents with BoloSign"}
            steps={steps}
          />
        </div>

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-20">
          <WhyChooseBolosign
            heading={"Why Choose BoloSign for Merging PDFs?"}
            description={`Our platform is designed with simplicity in mind, making it easy for anyone to merge PDF documents without any technical expertise. You can do it in a very quick and reliable manner ensuring that your documents are ready when you need them. Also, we prioritize your privacy and data protection by processing and storing your documents securely.`}
            reasons={reasons}
          />
        </div>

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-32">
          <PricingCards
            heading={"Looking for more than PDF tools?"}
            description={`Join 50,000+ teams who are already signing and accelerating their document workflow with BoloSign.`}
          />
        </div>

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-32">
          <TryBolosign
            heading={"Try BoloSign Free for 7 Days!"}
            bgColor={"bg-[#fee5de]"}
          />
        </div>

        <div className="w-full px-4 sm:px-8 mt-16 sm:mt-32">
          <OtherPdfTools
            heading={`Other PDF Editing Tools`}
            description={`Free online PDF tools to merge, split, rotate, compress, convert, and many more.
Manage your daily PDF tasks with our online PDF tools.`}
          />
        </div>

        {signaturePopup && (
          <SignaturePopup
            onPopupClose={() => setSignaturePopup(false)}
            source="MERGE_PDF"
          />
        )}

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-32 flex flex-col gap-12">
          <h2 className="text-center font-bold leading-[45px] sm:leading-[55px] lg:leading-[70px] text-[36px] sm:text-[48px] lg:text-[60px]">
            Frequently Asked Questions
          </h2>
          <div className="max-w-screen-lg mx-auto w-full flex flex-col gap-6">
            {faqData.map((faq, index) => (
              <FaqCard
                key={index}
                faqQuestion={faq.faqQuestion}
                faqAnswer={faq.faqAnswer}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MergePdf;
